import React from "react";
import { Container } from "react-bootstrap";
import DocumentMeta from "react-document-meta";
import HeaderBanner from "../Components/HeaderBanner";
import Config from "../config.json";
import Privacy from "../Images/Privacy.png";
import { t } from "../lib/utils";
import styles from "./PrivacyPolicy.module.css";

const page_meta = {
  language: "fa",
  copyright: "پژوهیار",
  description: "نرم‌افزار استناددهی",
  title: Config.title + " | حریم خصوصی",
  meta: {
    charset: "utf-8",
    name: {
      keywords: "پژوهیار",
    },
  },
};

const PrivacyPolicy = () => {
  return (
    <DocumentMeta {...page_meta}>
      <HeaderBanner
        header_color="#eee"
        image={Privacy}
        title={t("privacyPolicyHeaderTitle")}
        body={t("privacyPolicyHeaderBody")}
      />
      <Container className="overflow-hidden">
        <div className={`py-3 ${styles["cnt"]}`}>
          <div className={`text-left text-justify ${styles["cnt-en"]}`}>
            <h2>Pajoohyar Privacy Policy</h2>
            <hr />
            <h3>Overview</h3>
            <p>
              Pajoohyar is a customized version of{" "}
              <a className="l" href="https://www.zotero.org/">
                Zotero
              </a>
              . Pajoohyar is an open-source project committed to providing the
              best research tool available anywhere. Our philosophy is that what
              you put into Pajoohyar is yours, and one of our founding
              principles is to make sure you remain in control of your data and
              can share it how you like &mdash; or choose not to share it at
              all.
            </p>
            <p>
              <strong>
                We are an independent, nonprofit organization and have no
                financial interest in your private information.
              </strong>{" "}
              We fund further development by offering additional translators,
              styles, and courses of learning to novice researchers who find the
              software useful, not by selling data.
            </p>

            <h3>Data We Collect</h3>
            <p>
              Pajoohyar is designed as a local program that saves data to your
              own computer by default, and it doesn&rsquo;t require sharing any
              data with us to be usable. However, some of Pajoohyar&rsquo;s
              advanced features require you to supply us with information.
            </p>
            <ul>
              <li>
                We collect the information you voluntarily provide (e.g., your
                name and email address) if you create a Pajoohyar account and
                use optional Pajoohyar services. Pajoohyar can be used without
                creating an account.
              </li>
              <li>
                We collect the library data you upload if you choose to
                synchronize your library with the Pajoohyar servers. Syncing is
                entirely optional and is disabled by default.
                <br />
                We keep a record of the most recent IP addresses used to access
                your synchronized library in order to let you verify the
                security of your account.
              </li>
              <li>
                We log visits to our website, including IP address and browser,
                in order to prevent abuse and to diagnose technical issues. We
                retain these access logs for 90 days.
              </li>
              <li>
                We log requests made to our servers by Pajoohyar or third-party
                software, including IP address and client information, in order
                to prevent abuse, diagnose technical issues, and assess usage.
                We retain these logs for up to 90 days.
              </li>
              <li>We collect error reports that you submit.&nbsp;</li>
              <li>
                If you attempt to save something to Pajoohyar and the save
                fails, we collect information about the failure, including the
                URL, browser, and version information, so that we can more
                quickly fix site compatibility issues. We store this information
                for up to one week. No additional personally identifying
                information (e.g., username or IP address) is stored, and
                reports are generally only viewed in aggregate. This error
                reporting can be disabled in the Pajoohyar and Pajoohyar
                Connector preferences.
              </li>
            </ul>

            <h3>Library Statistics</h3>
            <p>
              Pajoohyar anonymizes and aggregates synchronized user and group
              library data to generate statistics on readership. This anonymized
              and aggregated data only include publicly available metadata
              (e.g., publication title and author). This data is never sold or
              made available in any forms other than ones offered publicly. We
              may also use this anonymized and aggregated user information for
              auditing, research, and analysis to operate and improve Pajoohyar
              services.
            </p>

            <h3>Disabling Automatic Requests</h3>
            <p>
              You can disable all automatic communication with Pajoohyar servers
              from the Pajoohyar and Pajoohyar Connector preferences:
            </p>
            <ul>
              <li>
                <strong>Syncing:</strong> Sync preferences &rarr; leave
                unconfigured or disable automatic syncing
              </li>
              <li>
                <strong>Automatic PDF metadata retrieval:</strong> General
                preferences &rarr; disable &ldquo;Automatically retrieve
                metadata for PDFs&rdquo;
                <ul>
                  <li>
                    We do not log any information about the contents of PDF
                    metadata requests.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Open-access PDF retrieval:</strong> General preferences
                &rarr; disable &ldquo;Automatically attach associated PDFs and
                other files when saving items&rdquo;
                <ul>
                  <li>
                    If a PDF can't be saved for an item with a DOI, Pajoohyar
                    will send the DOI to Pajoohyar servers to check for
                    open-access versions. We do not log the contents of these
                    requests. Disabling this preference will disable all
                    automatic attachment saving.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Broken site translator reporting:</strong> disable
                &ldquo;Report broken site translators&rdquo; in the Advanced
                pane of Pajoohyar and the Pajoohyar Connector
              </li>
              <li>
                <strong>Translator/style update checking:</strong> Advanced
                preferences &rarr; disable &ldquo;Automatically check for
                updated translators and styles&rdquo;
              </li>
              <li>
                <strong>Pajoohyar update checking:</strong> Advanced preferences
                &rarr; Config Editor &rarr; set app.update.auto to false
              </li>
              <li>
                Automatic update checking is strongly recommended for security
                and stability reasons.
              </li>
            </ul>
            <p>
              If automatic syncing or automatic translator/style updates are
              enabled, Pajoohyar will maintain a persistent connection to
              Pajoohyar servers when it is open in order to provide immediate
              updates. You can disable this connection by disabling both of
              those options or by setting extensions.zotero.streaming.enabled to
              false in the Config Editor.
              <br />
              If you use the Pajoohyar Connector without having Pajoohyar open,
              the Connector will make a daily request to Pajoohyar servers for
              information on available site translators. It will then download
              translators for the sites you visit. For example, if you load a
              New York Times article, the Connector will download Zotero&rsquo;s
              New York Times translator and cache it. If Pajoohyar doesn&rsquo;t
              have a translator for a specific site, no request will be made. No
              information on the specific pages you visit is transmitted, and
              subsequent requests won&rsquo;t be made for the same translator
              until you restart your browser or the translator is updated. You
              can avoid these requests by keeping Pajoohyar open while you
              browse the web.
            </p>

            <h3>Permissions Warnings</h3>
            <p>
              When using third-party platforms, we request the most restrictive
              permissions available that still allow Pajoohyar to perform its
              advertised functions. In some cases, the necessary permissions can
              sound a bit scary, so we want to explain why they&rsquo;re
              necessary.
            </p>

            <h3>Pajoohyar Connector</h3>
            <p>
              When installing the Pajoohyar Connector, your browser will warn
              you that the extension can &ldquo;Read and change all your data on
              the websites you visit&rdquo; (or similar). This is the standard
              permission that browser extensions that run on all pages require.
              Pajoohyar uses it to determine what content it can save on a given
              page and update the save button accordingly, as well as to provide
              advanced features such as automatic proxy redirection and
              automatic RIS/BibTeX import. No data is stored except when you
              choose to save a page to either your local or online Pajoohyar
              library.
            </p>

            <h3>Storage Purchases</h3>
            <p>
              We send two pieces of personal data to our payment processor at
              the time of purchase:
              <br />
              your Pajoohyar username, in order to associate your payment with
              your Pajoohyar account
              <br />
              your email address, so that the payment processor can send you a
              payment receipt
              <br />
              Pajoohyar does not collect or store your credit card, banking, or
              other payment information. When you enter your full name, billing
              address, and account numbers to complete a Pajoohyar purchase,
              this information passes directly to our payment processor, and it
              never touches Pajoohyar servers.
            </p>

            <h3>Support Interactions</h3>
            <p>
              Most Pajoohyar support occurs on the{" "}
              <a className="l" href="https://pajoohyar.ir/faq">
                FAQ webpage
              </a>{" "}
              or{" "}
              <a className="l" href="https://pajoohyar.ir/contact">
                call services
              </a>
              . You may be asked to submit an error report or debug output to
              help us troubleshoot problems. These reports contain technical
              information about your computer, such as your operating system and
              installed browser extensions, and may include incidental personal
              information such as URLs of sites you visited before or while
              generating the report. You can review the output of these reports
              before submitting them. We don&rsquo;t store any personal
              information (username, IP address) that links the report to you.
              Reports are stored for up to one year.
              <br />
              If you email us, we collect your email address and any other
              information you provide, and we may store your messages
              indefinitely to provide context for any future support requests
              you make.
            </p>

            <h3>Third-Party Services Used</h3>
            <p>
              Certain operations you perform in Pajoohyar may trigger requests
              to public third-party services such as Crossref or the Library of
              Congress for metadata retrieval. These third parties may log your
              IP address and search terms (e.g., DOI or ISBN) according to their
              privacy policies, but no other identifying information is
              provided.
              <br />
              When you save items to or update items in Pajoohyar, Pajoohyar may
              depend on your settings, connect to the associated sites to
              download metadata, or save PDFs or snapshots. This is equivalent
              to loading those sites in your browser, and similar privacy
              implications apply.&nbsp;
            </p>

            <h3>Backed-up Data</h3>
            <p>
              We make regular automated backups to protect against accidental
              loss of user data. These backups are intended for disaster
              recovery and would be accessed only in the event of significant
              data loss. Backups may be retained for up to 6 months.
            </p>

            <h3>Legally Compelled Disclosure</h3>
            <p>
              We may be legally required to comply with requests for data from
              law enforcement or government agencies.
            </p>

            <h3>Changes</h3>
            <p>
              We may update our privacy policies over time. Up-to-date
              information, including details of new features, will always be
              available on this page.
            </p>

            <h3>Questions</h3>
            <p>
              If you have any questions or concerns regarding Pajoohyar&rsquo;s
              privacy policies, please ask us by email at{" "}
              <a href="mailto:info@pajoohyar.ir">info@pajoohyar.ir</a>.
            </p>
          </div>
          <br />
          <hr />
          <br />

          {/* --------------------------- */}
          <div className="text-justify">
            <h3>1. مالکیت </h3>
            <p>
              این موافقت‌نامه حقوق محدودی به شما می‌دهد تا از این نرم‌افزار
              استفاده کنید. «نور» تنها مالک آن است و تمام حقوق و منافع مادی و
              معنوی آن را منحصراً برای خود محفوظ می‌دارد.
              <br /> «پژوهیار» نگارش تغییر یافته‌ای از زوترو1 است،که به صورت متن
              باز تحت مجوز نشر عمومی گنو2 (نسخه 3) عرضه می‌شود.
              <br /> این حقوق شامل ( و نه محدود به) کلیت نرم‌افزار و اجزاء آن
              (گرچه در آن اجزاء به‌خصوص، به حق نشر آن تصریح نشده باشد) و
              شیوه‌های فنی و تحقیقاتی به‌کار رفته در نرم‌افزار، متون الکترونیکی،
              فراداده‌های متنی و غیرمتنی، ابزارهای متنوع پژوهشی، فهرست‌های
              متنوع، صوت‌ها، تصاویر، فیلم‌ها، پویانمایی‌ها، نقشه‌ها، مدل‌ها،
              آمار و ارقام، جدول‌های نمایشی، صفحات گرافیکی، طراحی‌ها، و اجزای هر
              یک از آن‌ها، و منابع فنی برنامه و اجزای آن است و قوانین حق نشر و
              توزیع و تکثیر مصوب جمهوری اسلامی ایران و معاهدات بین‌المللی از آن
              حمایت می‌کند.
            </p>
            <h3>2. پروانه </h3>
            <p>
              این موافقت‌نامه به شما اجازه می‌دهد تا:
              <br />
              الف) نرم‌افزار را بر روی حد اکثر پنج رایانه که متعلق به شما و
              نزدیکان شماست (رایانهٔ خانه، محیط کار، یا همراه)، نصب و از آن
              استفاده نمایید.
              <br /> ب) با استفاده از ابزار پشتیبان گیری موجود در نرم‌افزار نسخه
              پشتیبان شخصی از کتابخانه «پژوهیار» تهیه کنید تا در صورت از دست
              رفتن اطلاعات به هر علت، با استفاده از ابزار بازیابی و فایل پشتیبان
              اطلاعات از دست رفته را به کتابخانه «پژوهیار» بازگردانید. لذا
              مسئولیت از دست رفتن احتمالی اطلاعات کاربری بر عهدهٔ شماست.
            </p>
            <h3>3. محدودیت‌های پروانه </h3>
            <p>
              الف) به غیر از آنچه در بخش 2 قید شد، شما مجاز نیستید پشتیبان‌های
              این نرم‌افزار را تولید یا توزیع کنید یا نرم‌افزار را به صورت
              الکترونیکی از یک کامپیوتر به کامپیوتر دیگر یا از طریق شبکه انتقال
              دهید.
              <br /> ب) شما مجاز به انجام کارهای ذیل نیستید: شکستن قفل
              نرم‌افزار، کشف رمز، مهندسی معکوس3، دیس‌اسمبل4، دی‌کامپایل5، یا هر
              گونه تحویل و تغییر در نرم‌افزار.
              <br /> ج) مجاز نیستید این نرم‌افزار را بفروشید، کرایه دهید، کرایه
              بگیرید، یا پروانهٔ بهره برداری آن را به شخص دیگری واگذار کنید.v{" "}
              <br />
              د) مجاز نیستید کد این نرم افزار را خارج از ضوابط مجوز نشر عمومی
              گنو مندرج در بند یک تغییر دهید یا اقتباسی مبتنی بر آن رقم بزنید.
              <br />
              ه) مجاز نیستید این نرم‌افزار را به کشوری صادر کنید که تحت تحریم
              قوانین اجرایی صادرات دولت جمهوری اسلامی ایران و مقررات مندرج در آن
              است.
              <br /> و) شما نمی‌توانید نسخه‌های واگذار شده به خود را به غیر از
              نزدیکان خود آن هم به تعداد مورد اشاره در بخش 2بند الف، برای دیگری
              نصب یا واگذار کنید. امکان پنج بار نصب، صرفاً برای شما و نزدیکان
              شما، جهت تسهیل بهره‌برداری از نرم‌افزار در محیط‌های متنوعی است که
              دارای رایانهٔ شخصی هستید.
            </p>
            <h3>4. ارتقاها </h3>
            <p>
              اگر این کپی از این نرم‌افزار (نسخه)، ارتقا یافتهٔ یک نسخه پیشین
              این نرم‌افزار است، این (کپی) بر مبنای پروانهٔ تعویض در اختیار شما
              قرار می‌گیرد.
              <br /> با نصب و استفاده از این کپی از برنامه، شما موافقت می‌کنید
              که به‌صورت داوطلبانه، موافقت‌نامهٔ پیشین خود را لغو کنید و اینکه
              دیگر از نسخهٔ قبلی این برنامه
              <br />
              استفاده نخواهید کرد و آن را به شخص دیگری واگذار نخواهید نمود. لازم
              است شما همهٔ کپی‌های نسخهٔ قبلی را که شامل هر گونه کپی مندرج در
              حافظۀ شماست، معدوم نمایید.
              <br /> در صورتی که «نور» یا یک شخص ثالث مجاز در رابطه با
              نرم‌افزاری که در ذیل این سند، پروانه‌اش به شما اعطا شده است،
              نرم‌افزار اضافی که نرم‌افزار (اصلی) را تکمیل می‌کند یا بسط می‌دهد،
              برای شما مهیا نماید، آن نرم‌افزار اضافی باید تحت شمول شرایط و
              مقررات این موافقت‌نامه باشد مگر اینکه در زمان تحویل، خلاف آن مشخص
              شده باشد.
              <br /> علی رغم آنچه در بالا گفته شد در صورتی که برای نصب و استفاده
              از نسخهٔ جدید، نیاز به نسخهٔ قبلی باشد شما مجاز هستید نسخهٔ قبلی
              را نگاه دارید و مجاز هستید از نسخهٔ قبلی استفاده کنید و از آن،
              نسخهٔ پشتیبان تهیه نمایید.
            </p>
            <h3>5. منابع الکترونیکی</h3>
            <p>
              الف) این نرم‌افزار شامل منابع الکترونیکی از قبیل کتابخانه‌های
              موضوعی (کتابخانه نور)، شیوه‌نامه‌ها و مترجم‌ها است.
              <br /> امتیاز تکثیر و توزیع الکترونیکی هر یک از این منابع، صرفاً
              در اختیار «نور» است.
              <br /> هر نوع توزیع و انتشار انبوه و غیر انبوه این منابع
              الکترونیکی، از طریق چاپ یا تکثیر الکترونیکی، درون نرم‌افزاری دیگر
              یا به طور مستقل ممنوع است و به تناسب موضوع، مشمول قوانین جاری
              جمهوری اسلامی ایران، معاهدات بین الملی و منطقه ای پذیرفته
              <br />
              شده از سوی دولت جمهوری اسلامی ایران و مجوز نشر عمومی گنو است.
              «نور»، حق تکثیر یا استفاده از این منابع الکترونیکی را جز در مورد
              استفادهٔ شخصی از نسخهٔ واحد این نرم افزار، به هیچ یک از کاربران
              نهایی نداده است.
              <br /> خارج از مفاد فوق‌الذکر در این تعهد، کاربران نمی‌توانند از
              این منابع در پایگاه‌های اینترنتی خویش یا اشخاص دیگر (اعم از حقیقی
              و حقوقی) استفاده کنند یا در نرم‌افزارهای دیگر قرار داده و تکثیر
              کنند، بفروشند یا اهداء نمایند یا به هر نحو دیگر در اختیار دیگران
              قرار دهند.
              <br /> استخراج انواع منابع محتوایی و فنی این نرم‌افزار از آن، به
              هر هدف از جمله فروش، انتشار و دیگر انواع بهره برداری ممنوع است.
              <br /> این ممنوعیت شامل تمامی اطلاعات موجود در برنامه، اعم از
              منابع مکتوب، صوتی، تصویری و هر نوع اطلاعات دیگر و هر بسته محتوایی
              که در برنامه از آن‌ها استفاده شده است، نیز است.
              <br /> ب) «نور»، اجازهٔ نشر الکترونیکی و فیزیکی منابع و نرم
              افزارهای خود از جمله منابع و همین نرم افزار را به هیچ شخص حقیقی یا
              حقوقی واگذار نکرده است.
              <br /> نحوه توزیع و تکثیر منابع در اختیار نهاده شده، تابع قرارداد
              اختصاصی و جداگانه‌ای است.
              <br /> برای اطلاع بیشتر از اهداف و فعالیت‌های «نور» به پایگاه ذیل
              مراجعه نمایید.
              <br /> <a href="https://www.noorsoft.org">www.noorsoft.org</a>
              <br />
              برای دسترسی به سایر منابع، راهنماها، از جمله راهنمای نصب و
              <br />
              فیلم‌های آموزشی «پژوهیار» هم به وبگاه‌های ذیل مراجعه فرمایید:
              <br /> <a href="https://www.pajoohyar.ir">www.pajoohyar.ir</a>
              <br />
            </p>
            <h3> 6. تضمین محدود:</h3>
            <p>
              الف) «نور» طی یک دورهٔ نود (90) روزه از تاریخ تحویل (همان‌گونه که
              کپی رسید شما گواه آن است) تضمین می‌کند که: <br /> اولاً این
              نرم‌افزار، هنگامی‌که با پیکربندی سخت‌افزاری و نرم‌افزاری توصیه
              شده، به طور صحیح (مطابق با دستور استفادهٔ ارائه شده به همراه آن)
              به‌کار رود، تمام ویژگی‌های معرفی شده در مستندات نرم افزار را
              دربرخواهد داشت.
              <br /> ثانیاً، حامل فیزیکی این نرم‌افزار، در به‌کارگیری عادی و
              صحیح دچار نقص اطلاعات و مشکلات مخل بهره‌برداری نشود.
              <br /> ب) نبود هیچ تضمین دیگری <br /> «نور» هر گونه تضمین دیگر به
              جز آنچه که در تضمین محدود پیشین اشاره شد، را نفی می‌کند خواه تصریح
              به نفی آن شده باشد یا تلویحاً به آن اشاره شده باشد؛ یا به هر نحو
              شامل تضمین‌های مربوط به قابلیت عرضه به بازار، تناسب برای هدفی خاص،
              همچنین نسبت به عدم نقض حقوق مالکیت، یا بهره برداری مخفیانه هیچ
              تضمینی در کار نیست.
              <br /> ج) اگر قانون قابل اجرا، متضمن هر گونه تضمین در رابطه با این
              نرم‌افزار باشد، همهٔ این تضمین‌ها مربوط به یک دورهٔ نود (90) روزه
              از تاریخ تحویل هستند.
              <br /> د) هیچ اطلاعات شفاهی یا کتبی یا توصیه، توسط «نور»، معامله
              کنندگان، توزیع کنندگان، نمایندگی‌های آن یا کارمندانش، که تضمینی را
              (برای «نور» ) ایجاد کند یا به طریقی محدودهٔ این تضمین را افزایش
              دهد معتبر نخواهد بود.
            </p>
            <h3> 7. جبران انحصاری خسارت</h3>
            <p>
              جبران انحصاری خسارت شما تحت بخش ششم (این قرارداد) عبارت است از
              عودت دادن این نرم‌افزار به همراه یک رونوشت از رسید شما و شرحی از
              مشکل، به مکانی که در آنجا آن را دریافت کرده‌اید.
              <br /> «نور» تلاش‌های معقولی را به‌کار خواهد بست تا بسته به انتخاب
              خود («نور»)، یک کپی جایگزین از این نرم‌افزار را که خالی از اشکال
              پیش آمده باشد، به شما بدهد یا رسانهٔ معیوب را تعویض کند، یا قیمتی
              را که برای خرید این نرم‌افزار پرداخته‌اید، به شما بازگرداند.
              <br /> چنانچه این نرم‌افزار به هر طریقی دست‌کاری شده باشد، و در
              اثر حادثه، بد به کار بردن و استفادهٔ نادرست آسیب دیده باشد، یا اگر
              نقص، از کاربرد این نرم‌افزار با یک پیکربندی سخت‌افزاری،
              نرم‌افزاری، غیر از پیکربندی توصیه شده، ناشی باشد، «نور» هیچ
              مسئولیتی نخواهد داشت.
            </p>
            <h3>8. چنانچه طرفین اذعان کنند</h3>
            <p>
              {" "}
              یا به صورت قانونی، معلوم شود هر یک از مفاد این موافقت‌نامه غیر
              معتبر یا به نحوی غیرقابل اجراست، مابقی شرایط و مفاد آن به طور کامل
              معتبر خواهد ماند.
            </p>
            <h3 style={{ color: "red" }}>"هشدار"</h3>
            <p>
              هرگونه نقض حقوق مالکیت فکری و مادی این اثر تخلف محسوب می‌شود و
              متخلف بر اساس قوانین حقوقی و جزایی حاکم بر موضوع، تحت پیگرد قانونی
              قرار خواهد گرفت.
            </p>
            <hr />
            <p style={{ padding: "0 20px" }}>
              1. zotero
              <br />
              2. رایج‌ترین مجوز نرم‌افزارهای متن‌باز است، که عموماً این پروانه
              را به نام جی‌پی‌ال که صورت خلاصه شده عبارت گنو پابلیک لایسنس(GNU
              General Public License) است نیز می‌خوانند. آخرین نسخه این مجوز
              نسخه ۳ است که در ۲۳ ژوئن ۲۰۰۷ منتشر شده است.
              <br />
              3. Reverse Engineering: به علمی گفته می‌شود که از پاسخ به سوال
              می‌رسند و در واقع فرآیند کشف اصول تکنولوژیکی یک دستگاه، شیئ یا یک
              سیستم است.
              <br />
              4 . Disassemble: به معنی خواندن کدهای خام زبان ماشین و درک این
              اطلاعات با اصطلاحات خاص دیس اسمبل.
              <br />
              5. Decompile: فرایندی که تلاش می کند سورس کد را در بعضی زبان های
              سطح بالا برای برنامه ای که فقط در حالت بایت کد یا کدهای ماشین است
              فراهم کند.
            </p>
          </div>
        </div>
      </Container>
    </DocumentMeta>
  );
};

export default PrivacyPolicy;
